import { createFileRoute, Link, useNavigate, useLocation } from "@tanstack/react-router";
import { IsolatedFlag } from "@/components/system/IsolatedFlag";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Users, Megaphone } from "lucide-react";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useAuth } from "@/hooks/useAuth";
import { NationRivalryWidget } from "@/components/social/NationRivalryWidget";
import { TenantWarsWidget } from "@/components/social/TenantWarsWidget";
import { NationPickerInline } from "@/components/social/NationPickerInline";
import { StreakBadge } from "@/components/social/StreakBadge";
import { FanTitleCard } from "@/components/social/FanTitleCard";
import { FeaturedFansPanel } from "@/components/social/FeaturedFansPanel";
import { PredictionReputationBadge } from "@/components/profile/PredictionReputationBadge";
import { FanDnaCard } from "@/components/social/FanDnaCard";
import { CommunityMoments } from "@/components/social/CommunityMoments";
import { FomoTicker } from "@/components/social/FomoTicker";
import { VictoryCard } from "@/components/social/VictoryCard";
import { GuerrillaInviteButton } from "@/components/viral/GuerrillaInviteButton";
import { CinematicMomentsStream } from "@/components/cinematic/CinematicMomentsStream";
import { WorldStatusBanner } from "@/components/global/WorldStatusBanner";
import { GlobalEnergyBar } from "@/components/global/GlobalEnergyBar";
import { StadiumAmbientLayer } from "@/components/stadium/StadiumAmbientLayer";
import { ViralChallengesPanel } from "@/components/challenges/ViralChallengesPanel";
import { AmbassadorRanking } from "@/components/ambassadors/AmbassadorRanking";
import { AmbassadorMissions } from "@/components/ambassadors/AmbassadorMissions";
import { NationalCrewsWidget } from "@/components/ambassadors/NationalCrewsWidget";
import { CommunityEnergyTicker } from "@/components/ambassadors/CommunityEnergyTicker";
import { JoinAmbassadorCard } from "@/components/ambassadors/JoinAmbassadorCard";
import { CreatorLinkBuilder } from "@/components/ambassadors/CreatorLinkBuilder";
import { CreatorBoostHalo } from "@/components/ambassadors/CreatorBoostHalo";
import { getMyBracket } from "@/lib/bracket.functions";
import { getMyViralStats } from "@/lib/viralReferral.functions";

export const Route = createFileRoute("/social")({
  // P0-AUTH-06 — guard moved to the component so we can wait for the
  // global auth bootstrap (`authReady`) before deciding. `beforeLoad`
  // ran before the auth hook hydrated on Android Chrome cold-start,
  // which made the route silently fail to mount.
  component: SocialPage,
});

function SocialPage() {
  const { t } = useLanguage();
  const { user, isAuthenticated, authReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    console.log("[SOCIAL_PAGE_MOUNT]");
  }, []);
  useEffect(() => {
    console.log("[SOCIAL_PAGE_RENDER]", { authReady, isAuthenticated });
  }, [authReady, isAuthenticated]);
  useEffect(() => {
    if (authReady && !isAuthenticated) {
      // P0-AUTH-10 — preserve current route so login/welcome can restore it
      // instead of dropping the user on Home.
      void navigate({ to: "/login", search: { from: location.pathname } as never });
    }
  }, [authReady, isAuthenticated, navigate, location.pathname]);
  // Hooks must run unconditionally — gate the fetches via `enabled`.
  const ready = authReady && isAuthenticated;
  const bracket = useQuery({ queryKey: ["bracket", "me"], queryFn: () => getMyBracket(), enabled: ready });
  const viral = useQuery({ queryKey: ["viral", "me", user?.id], queryFn: () => getMyViralStats(), enabled: ready && !!user });
  if (!ready) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </main>
    );
  }

  const preds = (bracket.data?.predictions ?? []) as Array<{ is_exact: boolean; points_awarded: number }>;
  const exact = preds.filter((p) => p.is_exact).length;
  const totalPoints = preds.reduce((acc, p) => acc + (p.points_awarded ?? 0), 0);
  const refs = Number(viral.data?.registered ?? 0);

  return (
    <main className="relative mx-auto max-w-3xl space-y-5 px-4 py-6">
      <StadiumAmbientLayer />
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-black">{t("social_hub_title")}</h1>
        </div>
        <FomoTicker compact />
      </header>

      <CommunityMoments />

      <IsolatedFlag name="WorldStatusBanner" flag="overlays"><WorldStatusBanner /></IsolatedFlag>
      <IsolatedFlag name="GlobalEnergyBar" flag="overlays"><GlobalEnergyBar /></IsolatedFlag>

      <IsolatedFlag name="CinematicMomentsStream" flag="cinematic"><CinematicMomentsStream /></IsolatedFlag>

      <FanTitleCard />

      <FanDnaCard shareable />

      <PredictionReputationBadge />

      <FeaturedFansPanel limit={10} />

      <div className="grid gap-4 md:grid-cols-2">
        <NationPickerInline />
        <NationRivalryWidget />
      </div>

      <TenantWarsWidget />

      <StreakBadge />

      <ViralChallengesPanel />

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Ambassador Program
          </h2>
          <Link
            to="/ambassadors"
            className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-3 py-1 text-[11px] font-bold uppercase tracking-wider hover:bg-muted/50"
          >
            <Megaphone className="h-3.5 w-3.5" /> Ver programa
          </Link>
        </div>
        <CommunityEnergyTicker />
        <JoinAmbassadorCard />
        <CreatorBoostHalo label="Creator Boost">
          <AmbassadorMissions />
        </CreatorBoostHalo>
        <CreatorLinkBuilder />
        <div className="grid gap-4 md:grid-cols-2">
          <AmbassadorRanking limit={6} />
          <NationalCrewsWidget />
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{t("victory_cards_title")}</h2>
        <div className="flex flex-wrap gap-4">
          {exact > 0 && (
            <VictoryCard
              emoji="🔮"
              headline={t("victory_exact_headline").replace("{n}", String(exact))}
              subline={t("victory_exact_sub")}
              accent="violet"
            />
          )}
          {totalPoints >= 10 && (
            <VictoryCard
              emoji="🏆"
              headline={t("victory_points_headline").replace("{n}", String(totalPoints))}
              subline={t("victory_points_sub")}
              accent="amber"
            />
          )}
          {refs >= 1 && (
            <VictoryCard
              emoji="🚀"
              headline={t("victory_referrals_headline").replace("{n}", String(refs))}
              subline={t("victory_referrals_sub")}
              accent="cyan"
            />
          )}
          {exact === 0 && totalPoints < 10 && refs === 0 && (
            <VictoryCard emoji="🌟" headline={t("victory_default_headline")} subline={t("victory_default_sub")} accent="rose" />
          )}
        </div>
      </section>

      <GuerrillaInviteButton />
    </main>
  );
}